import * as React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { get } from 'lodash';
import Grid from '@lendi-ui/grid';

import { DATE_FORMAT_FULL_MONTH_YEAR, FIELD_PATHS, FIELD_VALUES, PAYMENT_TYPE } from '@funnels/constants';
import { FormValues, FormValuesALIEO } from '@funnels/types';
import { getFrequencyLabel } from '@funnels/utils';
import formatDateWith from '@funnels/utils/src/formatDateWith';

import {
  CurrentInterestRateReadOnlyView,
  CurrentLenderReadOnlyView,
  FixedRateExpiryDateReadOnlyView,
  InterestOnlyPeriodReadOnlyView,
  InterestTypeReadOnlyView,
  InterestTypeTermReadOnlyView,
  MinimumRepaymentsReadOnlyView,
  OutstandingBalanceReadOnlyView,
  RemainingLoanTermReadOnlyView,
  RepaymentTypeReadOnlyView,
} from '../../../../Questions';
import { PaddedGridUnit } from '../style';

interface CurrentLoanPanelLayoutProps {
  currentValues: FormValues | FormValuesALIEO;
  liabilityIndex: number;
}

export function HomeLoanLayout(props: CurrentLoanPanelLayoutProps) {
  const { currentValues, liabilityIndex } = props;
  const isInterestOnly =
    get(currentValues, FIELD_PATHS.LIABILITY_REPAYMENT_TYPE(liabilityIndex), null) === FIELD_VALUES.INTEREST_ONLY;
  const interestType = get(currentValues, FIELD_PATHS.LIABILITY_INTEREST_TYPE(liabilityIndex));
  const interestTypeDuration = get(currentValues, FIELD_PATHS.LIABILITY_INTEREST_TYPE_DURATION(liabilityIndex));
  const repaymentFrequency = getFrequencyLabel(
    get(currentValues, FIELD_PATHS.LIABILITY_REPAYMENT_FREQUENCY(liabilityIndex))
  );
  const { siphonFixedRateExpRelease } = useFlags();
  const fixedTermExpiryDate = formatDateWith(
    get(currentValues, FIELD_PATHS.LIABILITY_FIXED_TERM_EXPIRY_DATE(liabilityIndex)),
    DATE_FORMAT_FULL_MONTH_YEAR
  );
  const repaymentType = get(currentValues, FIELD_PATHS.LIABILITY_REPAYMENT_TYPE(liabilityIndex), null);

  return (
    <Grid gutter="nil">
      <PaddedGridUnit size={1 / 2}>
        <CurrentLenderReadOnlyView
          value={get(currentValues, FIELD_PATHS.LIABILITY_FINANCIAL_INSTITUTION(liabilityIndex), undefined)}
        />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <CurrentInterestRateReadOnlyView
          title="Interest rate"
          value={get(currentValues, FIELD_PATHS.LIABILITY_INTEREST_RATE(liabilityIndex), undefined)}
        />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <OutstandingBalanceReadOnlyView
          title="Loan amount"
          value={get(currentValues, FIELD_PATHS.LIABILITY_OUTSTANDING_BALANCE(liabilityIndex), undefined)}
        />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        {repaymentType && <RepaymentTypeReadOnlyView value={PAYMENT_TYPE[repaymentType]} />}
      </PaddedGridUnit>
      {interestType === FIELD_VALUES.FIXED_RATE && (
        <>
          <PaddedGridUnit size={1 / 2}>
            <InterestTypeReadOnlyView value={interestType} />
          </PaddedGridUnit>
          <PaddedGridUnit size={1 / 2}>
            {siphonFixedRateExpRelease ? (
              <FixedRateExpiryDateReadOnlyView value={fixedTermExpiryDate} />
            ) : (
              <InterestTypeTermReadOnlyView title="Remaining fixed rate term" value={interestTypeDuration} />
            )}
          </PaddedGridUnit>
        </>
      )}
      {isInterestOnly ? (
        <>
          <PaddedGridUnit size={1 / 2}>
            <MinimumRepaymentsReadOnlyView
              after={` /${repaymentFrequency}`}
              value={get(currentValues, FIELD_PATHS.LIABILITY_REPAYMENT_VALUE(liabilityIndex), undefined)}
            />
          </PaddedGridUnit>
          <PaddedGridUnit size={1 / 2}>
            <InterestOnlyPeriodReadOnlyView
              value={get(currentValues, FIELD_PATHS.LIABILITY_INTEREST_ONLY_REMAINING_TERM(liabilityIndex), undefined)}
            />
          </PaddedGridUnit>
          <PaddedGridUnit size={1 / 2}>
            <RemainingLoanTermReadOnlyView
              title="Remaining term"
              value={get(currentValues, FIELD_PATHS.LIABILITY_REMAINING_TERM(liabilityIndex), undefined)}
            />
          </PaddedGridUnit>
        </>
      ) : (
        <>
          <PaddedGridUnit size={1 / 2}>
            <MinimumRepaymentsReadOnlyView
              after={`/${repaymentFrequency}`}
              value={get(currentValues, FIELD_PATHS.LIABILITY_REPAYMENT_VALUE(liabilityIndex), undefined)}
            />
          </PaddedGridUnit>
          <PaddedGridUnit size={1 / 2}>
            <RemainingLoanTermReadOnlyView
              title="Remaining term"
              value={get(currentValues, FIELD_PATHS.LIABILITY_REMAINING_TERM(liabilityIndex), undefined)}
            />
          </PaddedGridUnit>
        </>
      )}
    </Grid>
  );
}
